/**
 * DriveZone — Root App Component (Task 5 — payment routes added)
 *
 * Public:    /login, /register, /verify-otp
 * Protected: /app/*, /subscribe, /subscribe/success, /account
 */

import { useEffect, useState } from 'react'
import {
  BrowserRouter, Routes, Route, Navigate, useNavigate,
} from 'react-router-dom'

import { useAuthStore }         from '@/stores/authStore'
import { useSearchStore }       from '@/stores/searchStore'
import { useToast, ToastContainer } from '@/components/Toast'
import AuthGuard                from '@/components/AuthGuard'
import api                      from '@/lib/api'
import LoginPage                from '@/pages/LoginPage'
import RegisterPage             from '@/pages/RegisterPage'
import OTPPage                  from '@/pages/OTPPage'
import AppLayout                from '@/pages/AppLayout'
import SubscribePage            from '@/pages/SubscribePage'
import SubscribePending         from '@/pages/SubscribePending'
import SubscribeSuccess         from '@/pages/SubscribeSuccess'
import AccountPage              from '@/pages/AccountPage'
import ForgotPasswordPage       from '@/pages/ForgotPasswordPage'
import ResetPasswordPage        from '@/pages/ResetPasswordPage'

function SessionWatcher() {
  const navigate   = useNavigate()
  const { logout } = useAuthStore()
  const { showToast } = useToast()

  useEffect(() => {
    function handleInvalidated(e: Event) {
      const detail = (e as CustomEvent<{ message?: string }>).detail
      useSearchStore.getState().clearAll()
      logout()
      showToast(
        detail?.message ?? 'Your account was logged in from another device.',
        'error',
      )
      navigate('/login', { replace: true })
    }
    window.addEventListener('drivezone:session_invalidated', handleInvalidated)
    return () => window.removeEventListener('drivezone:session_invalidated', handleInvalidated)
  }, [navigate, logout, showToast])

  return null
}

// Temporary promo helper — sends visitors straight to /app/search instead of
// /login while PUBLIC_SEARCH_MODE is on. Remove once the promo ends; revert
// "/" back to a plain <Navigate to="/login" replace />.
function RootRedirect() {
  const { isAuthenticated } = useAuthStore()
  const [target, setTarget] = useState<string | null>(null)

  useEffect(() => {
    if (isAuthenticated) { setTarget('/app/search'); return }
    api
      .get<{ public_search_mode: boolean }>('/api/auth/public-config/')
      .then(({ data }) => setTarget(data.public_search_mode ? '/app/search' : '/login'))
      .catch(() => setTarget('/login'))
  }, [isAuthenticated])

  if (!target) return null
  return <Navigate to={target} replace />
}

function AppShell() {
  const { toasts, dismiss } = useToast()

  return (
    <>
      <SessionWatcher />
      <ToastContainer toasts={toasts} onDismiss={dismiss} />

      <Routes>
        {/* ── Public ──────────────────────────────────────── */}
        <Route path="/"                 element={<RootRedirect />} />
        <Route path="/login"            element={<LoginPage />} />
        <Route path="/register"         element={<RegisterPage />} />
        <Route path="/verify-otp"       element={<OTPPage />} />
        <Route path="/forgot-password"  element={<ForgotPasswordPage />} />
        <Route path="/reset-password"   element={<ResetPasswordPage />} />

        {/* ── Protected ───────────────────────────────────── */}
        <Route element={<AuthGuard />}>
          <Route path="/app/*"               element={<AppLayout />} />
          <Route path="/subscribe"           element={<SubscribePage />} />
          <Route path="/subscribe/pending"   element={<SubscribePending />} />
          <Route path="/subscribe/success"   element={<SubscribeSuccess />} />
          <Route path="/account"             element={<AccountPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  )
}
